'use client';

import { useEffect, useState } from 'react';

interface ScoreGaugeProps {
  score: number; // 0-100
  label?: string;
  size?: number;
}

function getColor(score: number): string {
  if (score < 40) return '#b91c1c'; // red-700
  if (score < 70) return '#b45309'; // amber-700
  return '#15803d'; // green-700
}

function getRating(score: number): string {
  if (score < 20) return 'Poor';
  if (score < 40) return 'Fair';
  if (score < 60) return 'Good';
  if (score < 80) return 'Great';
  return 'Excellent';
}

export default function ScoreGauge({ score, label = 'Score', size = 200 }: ScoreGaugeProps) {
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    const duration = 1000;
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedScore(Math.round(score * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [score]);

  const color = getColor(score);
  const rating = getRating(score);
  const r = (size - 20) / 2;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const circumference = Math.PI * r;
  const offset = circumference - (animatedScore / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`}>
        {/* Background arc */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }}
        />
        {/* Score number */}
        <text x={cx} y={cy - 15} textAnchor="middle" className="text-4xl font-extrabold" fill="#111827" fontSize="42" fontWeight="800">
          {animatedScore}
        </text>
        {/* Label */}
        <text x={cx} y={cy + 5} textAnchor="middle" fill="#6b7280" fontSize="14">
          {label}
        </text>
      </svg>
      <span className="text-lg font-bold mt-1" style={{ color }}>
        {rating}
      </span>
    </div>
  );
}
